import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

interface MapProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  children?: React.ReactNode;
  onMapLoad?: (map: google.maps.Map) => void;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  style?: React.CSSProperties;
  className?: string;
}

interface GoogleMapProps extends MapProps {
  apiKey: string;
}

export interface GoogleMapHandle {
  fitBounds: (bounds: google.maps.LatLngBounds, padding?: number) => void;
  panToAndZoom: (center: google.maps.LatLngLiteral, zoom: number) => void;
  getMap: () => google.maps.Map | undefined;
}

const Map = forwardRef<GoogleMapHandle, MapProps>(({
  center,
  zoom,
  children,
  onMapLoad,
  onClick,
  style,
  className,
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map>();
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    if (mapRef.current && !map.current) {
      map.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
        mapTypeId: 'roadmap',
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain'],
        },
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
        // Note: styles property is not allowed when mapId is present
        // Map styles must be controlled via Cloud Console when using mapId
      });

      if (onMapLoad) {
        onMapLoad(map.current);
      }
    }
  }, [center, zoom, onMapLoad]);

  // Manage click listener separately so it updates when the onClick prop changes
  useEffect(() => {
    if (!map.current) {return;}

    if (clickListenerRef.current) {
      google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }

    if (onClick) {
      clickListenerRef.current = map.current.addListener('click', onClick);
    }

    return () => {
      if (clickListenerRef.current) {
        google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
    };
  }, [onClick]);

  // Update center and zoom when props change
  useEffect(() => {
    if (map.current) {
      map.current.setCenter(center);
      map.current.setZoom(zoom);
    }
  }, [center, zoom]);

  // Expose map control methods to parent components
  useImperativeHandle(ref, () => ({
    fitBounds: (bounds: google.maps.LatLngBounds, padding = 50) => {
      if (map.current) {
        map.current.fitBounds(bounds, padding);
      }
    },
    panToAndZoom: (center: google.maps.LatLngLiteral, zoom: number) => {
      if (map.current) {
        map.current.panTo(center);
        // Small delay to ensure smooth animation
        setTimeout(() => {
          if (map.current) {
            map.current.setZoom(zoom);
          }
        }, 300);
      }
    },
    getMap: () => map.current,
  }), []);

  return (
    <div
      ref={mapRef}
      style={style}
      className={className}
    >
      {children}
    </div>
  );
});

Map.displayName = 'Map';

const GoogleMap = forwardRef<GoogleMapHandle, GoogleMapProps>(({
  apiKey,
  ...mapProps
}, ref) => {
  const render = useCallback((status: Status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <div className="h-full w-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading map...</p>
            </div>
          </div>
        );
      case Status.FAILURE:
        return (
          <div className="h-full w-full flex items-center justify-center bg-red-50 dark:bg-red-900/20">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400 font-medium">Failed to load map</p>
              <p className="text-sm text-red-500 dark:text-red-300">
                Please check your internet connection and API key
              </p>
            </div>
          </div>
        );
      default:
        return <Map {...mapProps} ref={ref} />;
    }
  }, [mapProps, ref]);

  return (
    <Wrapper
      apiKey={apiKey}
      libraries={['geometry', 'drawing', 'places', 'marker']}
      render={render}
    />
  );
});

GoogleMap.displayName = 'GoogleMap';

export default GoogleMap;